<script setup lang="ts">
// Tela "Indique e Ganhe" do Minha Conta V2 (Figma mobile 19771:42056 /
// desktop 19771:52523). Link de convite e lista de indicados reais, mesmos
// fluxos de pages/user/refers.vue — inclusive o link (useReferralLink()).
const { fetchUserProfile } = useUserData();

// paridade com pages/user/refers.vue: sem user_refers_active (ou com
// user_refers_coming_soon), a tela não fica acessível. O menu já trava o
// item; isto cobre deep-link direto ao modal.
const { isUserRefersEnabled, isUserRefersComingSoon } = useFeatureFlagsJson();
const { close } = useUserV2Modal();

watchEffect(() => {
  if (!isUserRefersEnabled.value || isUserRefersComingSoon.value) close();
});

// ---- Link de indicação (real: userAffiliationCode) --------------------------

const {
  isLinkCopied,
  copyLink: handleCopyLink,
  shareLink: handleShareLink,
} = useReferralLink();

async function handleEligibleGames() {
  await navigateTo('/games/providers/pgsoft?from_page=refer-friend', {
    external: false,
    open: { target: '_self' },
  });
}

// ---- Amigos indicados (real: POST /users/list-referrals) --------------------

interface ReferralItem {
  id?: number | string;
  first_name?: string;
  last_name?: string;
  created_at?: string;
}

const isLoadingRefers = ref<boolean>(false);
const referrals = ref<ReferralItem[]>([]);

async function getRefers() {
  const { ApiService } = useApiService();

  isLoadingRefers.value = true;

  await ApiService.post('/users/list-referrals', {
    page: 1,
    type: 'all',
    date_period: 'all',
  })
    .then((response) => {
      referrals.value = response.data?.data || [];
    })
    .catch(() => {
      referrals.value = [];
    })
    .finally(() => {
      isLoadingRefers.value = false;
    });
}

function referralName(item: ReferralItem): string {
  return (
    [item.first_name, item.last_name].filter(Boolean).join(' ') ||
    'Amigo indicado'
  );
}

function referralDate(item: ReferralItem): string {
  if (!item.created_at) return '';

  return new Date(item.created_at).toLocaleDateString('pt-BR');
}

onMounted(async () => {
  await fetchUserProfile();
  getRefers();
});
</script>

<template>
  <div class="flex flex-col gap-[12px]">
    <!-- Card "Ganhe R$ 30,00 de SALDO REAL" -->
    <div
      class="flex w-full flex-col gap-[8px] rounded-[10px] bg-v2-default/40 p-[12px]"
    >
      <div class="flex w-full items-center gap-[12px]">
        <div
          class="flex size-[32px] shrink-0 items-center justify-center rounded-[10px] bg-v2-default/40"
        >
          <!-- ícone "indique" (busto) no estilo do UserV2Icon -->
          <svg
            class="size-[20px] text-white"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M12 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-7 9a7 7 0 0 1 14 0" />
          </svg>
        </div>
        <p class="text-[14px] font-medium leading-[20px] text-white">
          Ganhe
          <b class="font-bold text-v2-success">R$ 30,00</b>
          de
          <b class="font-bold">SALDO REAL</b>
        </p>
      </div>

      <div class="w-full text-[12px] leading-[16px] text-v2-default-800">
        <p>
          Ganhe R$ 30,00 de Saldo real para cada amigo que você indicar para a
          apostatudo.bet.br. Para efetivar seu bônus, o seu amigo indicado deve
          <b class="font-bold text-white">
            acumular R$ 150,00 apostados em alguns jogos da PG Soft.
          </b>
        </p>
        <p>
          Jogos excluídos: Fortune Snake, Fortune Dragon, Futebol Fever, Chicky
          Run, Piggy Gold e Dragon Tiger Luck.
        </p>
      </div>

      <div class="border-solid w-full border-t border-v2-default/40" />

      <button
        class="flex h-[32px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-v2-default px-[12px] text-[12px] leading-[16px] text-v2-default-900"
        type="button"
        @click="handleEligibleGames"
      >
        <svg
          class="size-[20px] shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          viewBox="0 0 24 24"
        >
          <path d="M12 8v5m0 3h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Ver jogos elegíveis
      </button>

      <div class="flex w-full items-start gap-[8px]">
        <button
          class="flex h-[40px] min-w-0 flex-1 items-center justify-center gap-[8px] rounded-[12px] bg-v2-primary px-[16px] text-[14px] leading-[20px] text-v2-default-900"
          type="button"
          @click="handleCopyLink"
        >
          <UserV2Icon name="copy" />
          {{ isLinkCopied ? 'Link copiado!' : 'Copiar Link' }}
        </button>
        <button
          aria-label="Compartilhar link"
          class="flex h-[40px] w-[64px] shrink-0 items-center justify-center rounded-[12px] bg-v2-default px-[12px] text-white"
          type="button"
          @click="handleShareLink"
        >
          <svg
            class="size-[20px] shrink-0"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M12 14V4m0 0 4 4m-4-4-4 4M5 14v6h14v-6" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Card "Amigos Indicados" -->
    <div
      class="flex w-full flex-col gap-[8px] rounded-[10px] bg-v2-default/40 p-[12px]"
    >
      <div class="flex w-full items-center gap-[12px]">
        <div
          class="flex size-[32px] shrink-0 items-center justify-center rounded-[10px] bg-v2-default/40"
        >
          <svg
            class="size-[20px] text-white"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M12 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-7 9a7 7 0 0 1 14 0" />
          </svg>
        </div>
        <p class="text-[14px] font-medium leading-[20px] text-white">
          Amigos Indicados
        </p>
      </div>

      <div class="border-solid w-full border-t border-v2-default/40" />

      <!-- loading -->
      <div
        v-if="isLoadingRefers"
        class="flex w-full flex-col gap-[8px]"
      >
        <div
          v-for="i in 3"
          :key="i"
          class="h-[40px] w-full animate-pulse rounded-[10px] bg-v2-default/40"
        />
      </div>

      <!-- lista (o Figma só define o estado vazio; linhas seguem o mesmo
             estilo de card da tela) -->
      <div
        v-else-if="referrals.length"
        class="flex w-full flex-col gap-[8px]"
      >
        <div
          v-for="(item, index) in referrals"
          :key="item.id || index"
          class="flex min-h-[40px] w-full items-center justify-between gap-[8px] rounded-[10px] bg-v2-default-300/60 px-[12px] py-[8px]"
        >
          <p class="min-w-0 truncate text-[14px] leading-[20px] text-white">
            {{ referralName(item) }}
          </p>
          <p class="shrink-0 text-[12px] leading-[16px] text-white/60">
            {{ referralDate(item) }}
          </p>
        </div>
      </div>

      <!-- estado vazio (badge amarela) -->
      <div
        v-else
        class="flex h-[40px] w-full items-center justify-center rounded-[16px] bg-v2-warning/20 px-[16px]"
      >
        <p class="text-[14px] leading-[20px] text-v2-warning">
          Você ainda não indicou ninguém!
        </p>
      </div>
    </div>
  </div>
</template>
